"use client";
import { useTranslations } from "next-intl";

import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@heroui/spinner";
import { clsx } from "clsx";

import styles from "./history-search-bar.module.css";

import { useSearchMembers } from "~features/search-button/model/use-search-members";
import { UiSearchIcon } from "~shared/ui/search/icons/ui-search-icon";
import { UiSearchBannerGroup } from "~shared/ui/search/ui-search-banner-group";
import { SearchContestantsResponse } from "~shared/lib/api/dto";
import { OpenSearchBanner } from "~features/open-search-modals/open-search-banner/ui/search-banner.ui";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 320;

export function HistorySearchBar() {
  const t = useTranslations("history.search");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] =
    useState<SearchContestantsResponse>([]);
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    searchMembers,
    members,
    isPending,
    error,
    reset: resetSearch,
  } = useSearchMembers();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length >= MIN_QUERY_LENGTH) {
      searchMembers(debouncedQuery);
    } else {
      setResults([]);
      resetSearch?.();
    }
  }, [debouncedQuery, resetSearch, searchMembers]);

  useEffect(() => {
    if (members?.data) {
      setResults(members.data);
    }
  }, [members]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 120);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
  };

  const shouldShowResults = useMemo(
    () => isFocused && debouncedQuery.length >= MIN_QUERY_LENGTH,
    [debouncedQuery, isFocused],
  );

  return (
    <div className={styles.wrapper}>
      <label className={styles.inputShell}>
        <UiSearchIcon color="sky" />
        <input
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={styles.input}
          placeholder={t("placeholder")}
        />
        {query && (
          <button type="button" onClick={handleClear} className={styles.clear}>
            {t("clear")}
          </button>
        )}
      </label>

      <div
        className={clsx(
          styles.results,
          shouldShowResults && styles.resultsVisible,
        )}
        onMouseDown={(e) => {
          // keep results open when interacting with the list
          e.preventDefault();
          handleFocus();
        }}
      >
        {isPending ? (
          <div className={styles.state}>
            <Spinner size="sm" color="primary" />
            <span>{t("searching")}</span>
          </div>
        ) : error ? (
          <div className={styles.state}>{t("error")}</div>
        ) : shouldShowResults ? (
          <UiSearchBannerGroup
            results={results}
            renderBanner={(results) => {
              const { numeration, ...rest } = results;

              return (
                <OpenSearchBanner
                  key={`${rest.id}-${numeration}`}
                  numeration={numeration}
                  results={rest}
                />
              );
            }}
          />
        ) : (
          <div className={styles.state}>{t("tooShort", { min: MIN_QUERY_LENGTH })}</div>
        )}
      </div>
    </div>
  );
}
